import { useState, useEffect } from 'react';
import { IconCloudUpload, IconCircleCheck, IconAlertTriangle, IconAlertCircle, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import { StatCard } from '../components/cards';
import MainCard from '../components/MainCard';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AgentSyncData {
  id: number;
  name: string;
  email_address: string;
  distributor_name: string;
  level: number;
  level_1_name: string;
  level_2_name: string;
  level_3_name: string;
  level_4_name: string;
  level_5_name: string;
  cycle_synced_at: string | null;
}

interface AgentForSync {
  agent_role_id: number;
  username: string;
  name: string;
  email_address: string;
  cyclepay_branch_id: number;
  sub_distributor_name: string;
}

interface SyncResult {
  username: string;
  name: string;
  branchId: number;
  error?: string;
  reason?: string;
}

interface SyncResults {
  success: SyncResult[];
  failed: SyncResult[];
  skipped: SyncResult[];
  total: number;
}

interface Stats {
  total: number;
  last24h: number;
  last7d: number;
  last30d: number;
}

export function AgentSyncPage() {
  const { hasWriteAccess } = useAuth();
  const canSync = hasWriteAccess('cycle');

  const [data, setData] = useState<AgentSyncData[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [previewAgents, setPreviewAgents] = useState<AgentForSync[]>([]);
  const [syncResults, setSyncResults] = useState<SyncResults | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [dataRes, statsRes] = await Promise.all([
        api.get('/cycle/agent-sync'),
        api.get('/cycle/stats'),
      ]);
      setData(dataRes.data || []);
      setStats(statsRes.data || null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSyncDialog = async () => {
    setSyncDialogOpen(true);
    setPreviewLoading(true);
    setPreviewAgents([]);
    setSyncResults(null);
    setError('');

    try {
      const res = await api.post('/cycle/sync-agents-to-cycle', {
        mode: 'preview',
      });
      setPreviewAgents(res.data.data?.agents || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecuteSync = async () => {
    setSyncLoading(true);
    setError('');

    try {
      const agentRoleIds = previewAgents.map((a) => a.agent_role_id);
      const res = await api.post('/cycle/sync-agents-to-cycle', {
        mode: 'execute',
        agentRoleIds,
      });
      setSyncResults(res.data.data?.results || null);
      setSuccess(res.data.message || 'Sync completed');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCloseSyncDialog = () => {
    setSyncDialogOpen(false);
    setPreviewAgents([]);
    setSyncResults(null);
  };

  return (
    <div>
      <PageHeader
        title="Agent Sync"
        subtitle="Agents missing cycle sync timestamp"
        action={
          canSync ? (
            <Button onClick={handleOpenSyncDialog} disabled={loading}>
              <IconCloudUpload className="mr-2 h-4 w-4" />
              Sync Unsynced Agents
            </Button>
          ) : undefined
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {success}
            <button onClick={() => setSuccess('')} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      {stats && (
        <div className="flex gap-3 mb-4 w-full">
          <div className="flex-1 min-w-0">
            <StatCard title="Total Synced" value={stats.total} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Last 24 Hours" value={stats.last24h} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Last 7 Days" value={stats.last7d} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Last 30 Days" value={stats.last30d} />
          </div>
        </div>
      )}

      <MainCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-bold">ID</TableHead>
              <TableHead className="font-bold">Name</TableHead>
              <TableHead className="font-bold">Email</TableHead>
              <TableHead className="font-bold">Distributor</TableHead>
              <TableHead className="font-bold">Level</TableHead>
              <TableHead className="font-bold">Level 1</TableHead>
              <TableHead className="font-bold">Level 2</TableHead>
              <TableHead className="font-bold">Level 3</TableHead>
              <TableHead className="font-bold">Level 4</TableHead>
              <TableHead className="font-bold">Level 5</TableHead>
              <TableHead className="font-bold">Synced At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8">
                  <IconLoader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            )}
            {!loading && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  No agents found matching criteria
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              data.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/50">
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email_address}</TableCell>
                  <TableCell>{row.distributor_name}</TableCell>
                  <TableCell>{row.level}</TableCell>
                  <TableCell>{row.level_1_name}</TableCell>
                  <TableCell>{row.level_2_name}</TableCell>
                  <TableCell>{row.level_3_name}</TableCell>
                  <TableCell>{row.level_4_name}</TableCell>
                  <TableCell>{row.level_5_name}</TableCell>
                  <TableCell>{row.cycle_synced_at || 'NULL'}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </MainCard>

      <Dialog open={syncDialogOpen} onOpenChange={(open) => !open && handleCloseSyncDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {syncResults ? 'Sync Results' : 'Sync Agents to Cycle'}
            </DialogTitle>
          </DialogHeader>

          {previewLoading && (
            <div className="flex justify-center py-8">
              <IconLoader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {!previewLoading && !syncResults && (
            <>
              <Alert className="mb-4">
                <AlertDescription>
                  {previewAgents.length} agent(s) will be synced to Cycle API
                </AlertDescription>
              </Alert>

              {previewAgents.length > 0 && (
                <ScrollArea className="max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Username</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Branch ID</TableHead>
                        <TableHead>Sub-Distributor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewAgents.slice(0, 50).map((agent) => (
                        <TableRow key={agent.agent_role_id}>
                          <TableCell>{agent.username}</TableCell>
                          <TableCell>{agent.name || agent.username}</TableCell>
                          <TableCell>{agent.email_address || '-'}</TableCell>
                          <TableCell>{agent.cyclepay_branch_id}</TableCell>
                          <TableCell>{agent.sub_distributor_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}

              {previewAgents.length > 50 && (
                <p className="text-sm text-muted-foreground mt-2">
                  ... and {previewAgents.length - 50} more
                </p>
              )}
            </>
          )}

          {syncResults && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant="success">
                  <IconCircleCheck className="mr-1 h-3 w-3" />
                  Success: {syncResults.success.length}
                </Badge>
                <Badge variant="warning">
                  <IconAlertTriangle className="mr-1 h-3 w-3" />
                  Skipped: {syncResults.skipped.length}
                </Badge>
                <Badge variant="destructive">
                  <IconAlertCircle className="mr-1 h-3 w-3" />
                  Failed: {syncResults.failed.length}
                </Badge>
              </div>

              {syncResults.failed.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <p className="font-semibold mb-1">Failed Agents:</p>
                    {syncResults.failed.slice(0, 10).map((f, i) => (
                      <p key={i} className="text-sm">{f.username}: {f.error}</p>
                    ))}
                    {syncResults.failed.length > 10 && (
                      <p className="text-sm">... and {syncResults.failed.length - 10} more</p>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {syncResults.skipped.length > 0 && (
                <Alert variant="warning">
                  <AlertDescription>
                    <p className="font-semibold mb-1">Skipped (Already Exist):</p>
                    {syncResults.skipped.slice(0, 5).map((s, i) => (
                      <p key={i} className="text-sm">{s.username}: {s.reason}</p>
                    ))}
                    {syncResults.skipped.length > 5 && (
                      <p className="text-sm">... and {syncResults.skipped.length - 5} more</p>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseSyncDialog}>
              {syncResults ? 'Close' : 'Cancel'}
            </Button>
            {!syncResults && previewAgents.length > 0 && (
              <Button onClick={handleExecuteSync} disabled={syncLoading}>
                {syncLoading ? (
                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <IconCloudUpload className="mr-2 h-4 w-4" />
                )}
                {syncLoading ? 'Syncing...' : `Sync ${previewAgents.length} Agents`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
